import {
    IService,
    ISettings,
    IURLParameters,
    ParameterSchema,
    ParameterUrlParser,
    SettingsParametersSchema, SystemComponentType
} from '@gongxigames/framework/core/types';
import {injectable} from '@gongxigames/framework/core/installer';
import {IStorage, storage} from '@gongxigames/framework/core/fragments/LocalStorage';
import {URLParameters} from '@gongxigames/framework/core/fragments/URLParameters';
import {consoleLogger} from '@gongxigames/framework/core/fragments/ConsoleLogger';
import {CoreDI} from '@gongxigames/framework/core/configs';
import {errorReporter} from '@gongxigames/framework/core/utils';

@injectable()
export class Settings<Parameters = Record<string, any>> implements ISettings<Parameters>, IService {
    public static NAME: symbol = CoreDI.Settings;
    public type: SystemComponentType = 'service';
    public static registerSettingsParametersSchema<Parameters>(parametersSchema: SettingsParametersSchema<Parameters>): void {
        Settings.schemas.push(parametersSchema);
    }

    private static schemas: SettingsParametersSchema<any>[] = [];

    private readonly parameters: Parameters = {} as Parameters;
    private readonly schema: Record<string, ParameterSchema<any>> = {};
    private urlParameters!: IURLParameters;
    private storage: IStorage = storage;

    constructor() {
        this.urlParameters = new URLParameters(window.location.search.substring(1));

        Settings.schemas.forEach(schema => this.addParametersSchema(schema));
    }

    public addParametersSchema<Parameters>(parametersSchema: SettingsParametersSchema<Parameters>): void {
        for (const key in parametersSchema) {
            if (parametersSchema.hasOwnProperty(key)) {
                const name = key as any;
                const schema = parametersSchema[key];
                let value;

                if (!schema) {
                    throw new Error(`${key} parameter already exists!`);
                }

                this.schema[name] = schema;

                if (schema.keepInStorage && this.storage.getItem(name) !== undefined) {
                    try {
                        const restoredValue: any = JSON.parse(this.storage.getItem(name)!);

                        if (restoredValue !== null) {
                            value = restoredValue;
                        }
                    } catch (e) {
                        consoleLogger.error(`Not restored parameter ${name} from storage.`);
                    }
                }

                if (schema.urlParser) {
                    const valueFromURL: any = this.parseURLParameter(name, schema.urlParser) ?? value;

                    if (schema.urlParser.debugOnly) {
                        /* develblock:start */
                        value = valueFromURL;
                        /* develblock:end */
                    } else {
                        value = valueFromURL;
                    }
                }

                if (value !== undefined) {
                    this.set(name, value);
                }
            }
        }
    }

    public has<K extends keyof Parameters>(name: K): boolean {
        this.checkParameterSchema(name);

        return (this.parameters as Object).hasOwnProperty(name) && this.parameters[name] !== undefined;
    }

    public set<K extends keyof Parameters>(name: K, value: Parameters[K]): void {
        this.checkParameterSchema(name);

        const {readonly, keepInStorage} = this.schema[name as string];

        if (readonly && this.has(name)) {
            throw new Error(`The value of this parameter ${name.toString()} already exists and cannot be overwritten! The parameter is read-only.`);
        }

        this.parameters[name] = value;

        if (keepInStorage) {
            this.storage.setItem(name as string, value as any);
        }

        errorReporter.setTags({[`setting:${name}`]: String(value)});
    }

    public setIfPossible<K extends keyof Parameters>(name: K, value: Parameters[K]): void {
        try {
            this.set(name, value);
        } catch(e) {
            consoleLogger.warn(e);
        }
    }

    public get<K extends keyof Parameters>(name: K): Parameters[K] {
        this.checkParameterSchema(name);

        const parameterSchema = this.schema[name as string];

        if (!this.has(name) && parameterSchema.hasOwnProperty('defaultValue')) {
            return parameterSchema.defaultValue;
        }

        return this.parameters[name];
    }

    public getAllProperties(): Parameters {
        const parameters: Parameters = {} as Parameters;

        Object.keys(this.schema).forEach(key => {
            const name: keyof Parameters = key as keyof Parameters;

            parameters[name] = this.get(name);
        });

        return parameters;
    }

    private checkParameterSchema<K extends keyof Parameters>(name: K): void {
        const schema = this.schema[name as string];

        if (!schema) {
            throw new Error(`No schema added for this (${name as string}) parameter. Before using this parameter, add a schema for it!`);
        }
    }

    private parseURLParameter(key: string, urlParameterParser: ParameterUrlParser<any>): any {
        const {
            alias = key,
            typePropertyInURL = 'string',
            parseValue = (value: any) => value
        } = urlParameterParser;
        let names;

        if (!Array.isArray(alias)) {
            names = [alias];
        } else {
            names = [...alias];
        }

        let value;

        names.forEach(name => {
            if (this.urlParameters.hasParameter(name as string)) {
                const valueFromUrl = this.urlParameters.getParameter(name as string, typePropertyInURL);

                value = parseValue(valueFromUrl);
            }
        });

        return value;
    }

    public setParameters(parameters: Partial<Parameters>): void {
        for (const key in parameters) {
            if (parameters.hasOwnProperty(key) && parameters[key] != null) {
                try {
                    this.set(key, parameters[key] as any);
                } catch (e) {
                    consoleLogger.error(e);
                    continue;
                }
            }
        }
    }
}
